<template>
  <div class="flex flex-1 flex-col overflow-y-auto">
    <template v-if="step === 'initial'">
      <div class="mb-4 flex flex-col items-center pt-4">
        <p class="mb-4 text-center font-medium">
          Sale Complete
        </p>
        <p class="mb-2 text-center text-3xl font-bold">
          {{
            formatToMerchantCurrencyString(
              invoice.amountBalancedTotal
            )
          }}
        </p>

        <PaymentDetailChip
          v-if="invoice.paidBy"
          :payment="invoice.paidBy"
          large
          class="self-center"
        />
      </div>

      <div
        v-if="
          invoice.amountBalancedGratuity ||
          invoice.amountBalancedCashback
        "
        class="flex flex-col px-7"
      >
        <StatRow
          label="Subtotal"
          :value="invoice.amountBalancedGross"
          type="currency"
          large
        />
        <StatRow
          v-if="invoice.amountBalancedGratuity"
          label="Gratuity"
          :value="invoice.amountBalancedGratuity"
          type="currency"
          large
        />
        <StatRow
          v-if="invoice.amountBalancedCashback"
          label="Cashback"
          :value="invoice.amountBalancedCashback"
          type="currency"
          large
        />
      </div>
      <div class="p-4">
        <template v-if="isTransactionReceiptsEnabled">
          <ActionItemSmall
            label="Email Receipt"
            :icon="IconEmailReceipt"
            @click="handleGotoEmail"
          />
          <ActionItemSmall
            v-if="organizationConfig?.smsServiceEnabled"
            label="SMS Receipt"
            :icon="IconSMSReceipt"
            @click="handleGotoSMS"
          />
          <ActionItemSmall
            v-if="printingSupported"
            label="Print Receipt"
            :icon="IconPrintReceipt"
            @click="handlePrintReceipt"
          />
        </template>
        <ActionButtonSecondary
          class="w-full"
          :class="
            isTransactionReceiptsEnabled ? 'mt-4' : ''
          "
          @click="handleClose"
        >
          Continue
        </ActionButtonSecondary>
      </div>
    </template>

    <template v-if="step === 'email'">
      <div>
        <DrawerHeader @close="handleBack">
          Email Receipt
        </DrawerHeader>
        <ReceiptEmailForm
          :object-id="orderId"
          receipt-type="order"
          class="px-4 pb-4"
          @submit-success="handleClose"
        />
      </div>
    </template>
    <template v-else-if="step === 'sms'">
      <div>
        <DrawerHeader @close="handleBack">
          SMS Receipt
        </DrawerHeader>
        <ReceiptSMSForm
          :object-id="orderId"
          receipt-type="order"
          class="px-4 pb-4"
          @submit-success="handleClose"
        />
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import {
  IconEmailReceipt,
  IconSMSReceipt,
  IconPrintReceipt,
} from "@/icons"

import { retrieveOrderReceipt } from "@/services/orders/retrieveOrderReceipt"
import { IPrinter } from "@/androidPlugins/IPrinter"
import { useOrganizationConfig } from "@/services/organizations/retrieveOrganizationConfig"
import type { CreateTransactionResponseInvoice } from "@/services/createTransaction"

const props = defineProps<{
  orderId: string
  invoice: CreateTransactionResponseInvoice
}>()

const emit = defineEmits<{
  (event: "done"): void
}>()

const { formatToMerchantCurrencyString } = useCurrency()
const { organizationConfig } = useOrganizationConfig()
const { isTransactionReceiptsEnabled } = useTenant()

const { startLoading, stopLoading } =
  usePageBlockingLoading()
const { printingSupported } = useDevice()

const step = ref<"initial" | "email" | "sms">("initial")
const handleGotoEmail = () => {
  step.value = "email"
}
const handleGotoSMS = () => {
  step.value = "sms"
}

const handleBack = () => {
  step.value = "initial"
}

const handleClose = () => {
  emit("done")
}

onMounted(() => {
  step.value = "initial"
})

const handlePrintReceipt = async () => {
  startLoading()
  try {
    const { receipt, webReceiptUrl } =
      await retrieveOrderReceipt(props.orderId)
    await IPrinter.printStr({
      str: receipt,
      footerQrCodeContent: webReceiptUrl,
    })
    handleClose()
  } catch {
    toast.error("Failed to generate receipt")
  } finally {
    stopLoading()
  }
}
</script>
